// The team-member session write, moved from
// `src/hooks/mutation/useMutateUpdateOtherUserSession.ts` (now a delegate) with
// the session-header modal cores. Body is byte-identical apart from
// the two prescribed crossings:
//
//  - **Cohort trio** — through `useSessionReviewCohort` (ruling 32's
//    `useSessionAccessContext` member). Note this hook reads the trio ONLY to
//    forward it to the linked-sessions invalidation; the mutation itself is
//    driven entirely by its input, which already carries `teamId`/`memberId`.
//  - **`toast` ×2** — `/data` is headless. Both the success and the
//    destructive toast are app reactions, fired through `onSessionUpdated` /
//    `onUpdateSessionError` exactly where they fired. The error copy
//    interpolates `error.message`, which is why this is the T2 shape of the
//    toast ladder (bodied delegate) rather than its T1 `meta` form.
//
// `mutationKey: ['UpdateOtherUserSession']` was an INLINE LITERAL. It is now
// `UPDATE_OTHER_USER_SESSION` in `@heidi/session/contracts` — a relocation of an
// existing identity, byte-identical, not a new key.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { logger } from '@heidi/runtime/logger';

import {
  GET_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  UPDATE_OTHER_USER_SESSION,
  type QuerySessionResponse,
  type UpdateTeamMemberSessionRequest,
} from '../../contracts';
import { updateSessionForOtherUser } from '../api/session';
import { invalidateTeamMemberSessionsQuery } from '../query/sessionsListCache';
import { invalidateQueryLinkedSessionsByProfileId } from '../query/useQueryLinkedSessionsByProfileId';
import { useSessionReviewCohort } from '../sessionReviewCohort';

export interface UpdateOtherUserSessionInput {
  sessionId: string;
  teamId: string;
  memberId: string;
  input: UpdateTeamMemberSessionRequest;
}

export type UseMutateUpdateOtherUserSessionPorts = {
  /** App-reaction port: fired where the success toast fired. */
  onSessionUpdated?: () => void;
  /** App-reaction port: fired where the destructive toast fired. */
  onUpdateSessionError?: (error: Error) => void;
};

export function useMutateUpdateOtherUserSession(
  isSessionLinked?: boolean,
  patientProfileId?: string,
  { onSessionUpdated, onUpdateSessionError }: UseMutateUpdateOtherUserSessionPorts = {}
) {
  const queryClient = useQueryClient();
  const {
    isReviewingOtherUser,
    teamId: cohortTeamId,
    memberId: selectedReviewUser,
  } = useSessionReviewCohort();

  return useMutation({
    mutationKey: [UPDATE_OTHER_USER_SESSION],
    mutationFn: async ({
      sessionId,
      teamId,
      memberId,
      input,
    }: UpdateOtherUserSessionInput) => {
      return await updateSessionForOtherUser({
        sessionId,
        teamId,
        memberId,
        input,
      });
    },
    onMutate: async ({ sessionId, teamId, memberId }: UpdateOtherUserSessionInput) => {
      await queryClient.cancelQueries({
        queryKey: [
          GET_TEAM_MEMBER_SESSION_BY_ID,
          { teamId },
          { memberId },
          { sessionId },
        ],
      });
      await queryClient.cancelQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId }],
      });
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<QuerySessionResponse>(
        [
          GET_TEAM_MEMBER_SESSION_BY_ID,
          { teamId: variables.teamId },
          { memberId: variables.memberId },
          { sessionId: variables.sessionId },
        ],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            ...data.data,
          };
        }
      );
      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId: variables.sessionId }],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            ...data.data,
          };
        }
      );

      if (isSessionLinked) {
        void invalidateQueryLinkedSessionsByProfileId({
          patientProfileId: patientProfileId ?? '',
          isReviewingOtherUser,
          teamId: cohortTeamId,
          selectedReviewMember: selectedReviewUser,
        });
      }

      onSessionUpdated?.();
    },

    onSettled: (_, __, variables) => {
      // Invalidate relevant queries after mutation settles
      void invalidateTeamMemberSessionsQuery(variables.teamId, variables.memberId);
    },

    onError: (error, variables) => {
      logger.error('Error updating session:', error);
      void queryClient.invalidateQueries({
        queryKey: [
          GET_TEAM_MEMBER_SESSION_BY_ID,
          { teamId: variables.teamId },
          { memberId: variables.memberId },
          { sessionId: variables.sessionId },
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId: variables.sessionId }],
      });
      onUpdateSessionError?.(error);
    },
  });
}
